import {
  AGE_GROUP_LABELS,
  AGE_GROUPS,
  BELT_NAMES,
  GRADES,
  type AgeGroup,
  type ApprovalEventDto,
  type CurriculumDto,
  type Grade,
  type GradeResponse,
  type StudentTechniqueDto,
  type UserDto,
} from '@bjj/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { api } from '../api/client';
import { useApiMutation } from '../api/hooks';
import { useAuth } from '../auth/AuthContext';
import { DataTable } from '../components/DataTable';
import { PromotionCard } from '../components/Certificates';
import { ParentsCard } from '../components/Parents';
import {
  Badge,
  Card,
  ConfirmButton,
  ErrorMessage,
  formatDate,
  gradedWhen,
  GradeBadge,
  gradeEventText,
  Loading,
  PageHeader,
  ProgressBar,
} from '../components/ui';
import { MembershipActions } from './StudentsPage';

export function StudentDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const [message, setMessage] = useState<string | null>(null);
  const [overrideReason, setOverrideReason] = useState('');
  const key = ['students', id ?? ''];
  const detail = useQuery({
    queryKey: key,
    queryFn: () => api.request<{ user: UserDto; curriculum: CurriculumDto }>(`/students/${id}`),
  });
  const history = useQuery({
    queryKey: ['approvals', 'student', id],
    queryFn: () =>
      api
        .request<{ items: ApprovalEventDto[] }>(`/approvals?studentId=${id}&limit=100`)
        .then((r) => r.items),
  });
  const grade = useApiMutation<
    { techniqueId: string; grade: Grade; override?: boolean },
    GradeResponse
  >(
    ({ techniqueId, grade: g, override }) => ({
      path: '/approvals',
      method: 'POST',
      body: {
        studentId: id,
        techniqueId,
        grade: g,
        ...(override ? { override: true, reason: overrideReason } : {}),
      },
    }),
    [key, ['approvals'], ['students']],
  );
  const rescind = useApiMutation<{ techniqueId: string }>(
    ({ techniqueId }) => ({
      path: '/approvals/rescind',
      method: 'POST',
      body: { studentId: id, techniqueId, reason: 'Grade removed by owner from dashboard' },
    }),
    [key, ['approvals'], ['students']],
  );

  const setGroup = useApiMutation<AgeGroup>(
    (ageGroup) => ({ path: `/admin/users/${id}`, method: 'PATCH', body: { ageGroup } }),
    [key, ['students']],
  );

  const removeAvatar = useApiMutation<void>(
    () => ({ path: `/admin/users/${id}/avatar`, method: 'DELETE' }),
    [key, ['students']],
  );
  const setHidden = useApiMutation<boolean>(
    (leaderboardHidden) => ({
      path: `/admin/users/${id}`,
      method: 'PATCH',
      body: { leaderboardHidden },
    }),
    [key, ['students'], ['leaderboards']],
  );

  if (detail.isLoading) return <Loading />;
  if (detail.error || !detail.data)
    return <ErrorMessage error={detail.error ?? new Error('Not found')} />;
  const { user: student, curriculum } = detail.data;
  const categoryName = new Map(curriculum.categories.map((c) => [c.id, c.name]));

  const reasonReady = overrideReason.trim().length >= 3;
  // Grade buttons: the current grade is shown filled. A locked, ungraded technique needs its
  // prerequisites at A first, unless the owner records an override reason above.
  const actions = (t: StudentTechniqueDto) => {
    const locked = t.status === 'locked' && !t.grading;
    const override = locked && isOwner && reasonReady;
    return (
      <div className="btn-row">
        <span className="grade-buttons" role="group" aria-label={`Grade ${t.name}`}>
          {[...GRADES].reverse().map((g) => {
            const current = t.grading?.grade === g;
            return (
              <button
                key={g}
                type="button"
                className={`btn small ${current ? `current grade-${g}` : ''}`}
                aria-label={`Grade ${g}`}
                aria-pressed={current}
                title={
                  locked && !override
                    ? isOwner
                      ? 'Needs an A on the prerequisites, or enter an override reason above'
                      : 'Needs an A on the prerequisites'
                    : undefined
                }
                disabled={
                  current || grade.isPending || student.status !== 'active' || (locked && !override)
                }
                onClick={() =>
                  grade.mutate(
                    { techniqueId: t.id, grade: g, override },
                    {
                      onSuccess: (r) =>
                        setMessage(
                          `${r.technique.name}: ${r.student.name} graded ${r.grade}${
                            override ? ' (override recorded)' : ''
                          }.`,
                        ),
                    },
                  )
                }
              >
                {g}
              </button>
            );
          })}
        </span>
        {isOwner && t.grading ? (
          <ConfirmButton
            confirmLabel="Remove grade?"
            onConfirm={() => rescind.mutate({ techniqueId: t.id })}
          >
            Remove
          </ConfirmButton>
        ) : null}
      </div>
    );
  };

  return (
    <>
      <PageHeader
        title={student.name}
        subtitle={
          <>
            {student.managed ? 'No login (parent follows)' : student.email} ·{' '}
            <Badge value={student.status} /> ·{' '}
            {student.ageGroup ? AGE_GROUP_LABELS[student.ageGroup] : ''} · joined{' '}
            {formatDate(student.createdAt)} · {BELT_NAMES[student.belt ?? 'white']}
          </>
        }
        actions={
          isOwner ? (
            <div className="btn-row">
              <select
                aria-label="Group"
                value={student.ageGroup ?? 'adults'}
                onChange={(e) => setGroup.mutate(e.target.value as AgeGroup)}
                title="Kids and adults have separate curricula and leaderboards"
              >
                {AGE_GROUPS.map((g) => (
                  <option key={g} value={g}>
                    {AGE_GROUP_LABELS[g]}
                  </option>
                ))}
              </select>
              <label className="checkbox" style={{ margin: 0 }}>
                <input
                  type="checkbox"
                  checked={!student.leaderboardHidden}
                  onChange={(e) => setHidden.mutate(!e.target.checked)}
                />{' '}
                Show on leaderboards
              </label>
              <MembershipActions
                userId={student.id}
                status={student.status}
                invalidate={[key, ['students']]}
              />
            </div>
          ) : null
        }
      />
      {student.avatarUrl ? (
        <Card title="Profile picture">
          <div className="btn-row">
            <img
              src={`/api/v1${student.avatarUrl}`}
              alt={`${student.name}'s profile picture`}
              width={96}
              height={96}
              style={{ borderRadius: 4, objectFit: 'cover' }}
            />
            {isOwner ? (
              <ConfirmButton confirmLabel="Remove picture?" onConfirm={() => removeAvatar.mutate()}>
                Remove photo
              </ConfirmButton>
            ) : null}
          </div>
          <ErrorMessage error={removeAvatar.error} />
        </Card>
      ) : null}
      <PromotionCard student={student} curriculum={curriculum} />
      {isOwner ? <ParentsCard studentId={student.id} studentName={student.name} /> : null}
      <div className="grid cols-2">
        <Card title="Overall completion">
          <ProgressBar percent={curriculum.progress.overall.percent} />
          <p className="muted">
            {curriculum.progress.overall.mastered} of {curriculum.progress.overall.total} techniques
            mastered
          </p>
        </Card>
        <Card title="By category">
          {curriculum.categories.map((c) => {
            const p = curriculum.progress.byCategory[c.id];
            return p ? (
              <div key={c.id} style={{ marginBottom: 8 }}>
                <div>{c.name}</div>
                <ProgressBar percent={p.percent} label={`${c.name} completion`} />
              </div>
            ) : null;
          })}
        </Card>
      </div>
      <Card title="Techniques">
        {message ? (
          <div className="alert success" role="status">
            {message}
          </div>
        ) : null}
        <ErrorMessage error={grade.error ?? rescind.error} />
        {isOwner ? (
          <div className="field" style={{ maxWidth: 460 }}>
            <label htmlFor="override-reason">
              Override reason (owner only: lets you grade a technique whose prerequisites aren't at
              A)
            </label>
            <input
              id="override-reason"
              type="text"
              maxLength={500}
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
            />
          </div>
        ) : null}
        <DataTable
          rows={curriculum.techniques}
          rowKey={(t) => t.id}
          columns={[
            { key: 'name', header: 'Technique', render: (t) => t.name },
            { key: 'cat', header: 'Category', render: (t) => categoryName.get(t.categoryId) ?? '' },
            {
              key: 'status',
              header: 'Grade',
              render: (t) =>
                t.grading ? (
                  <GradeBadge grade={t.grading.grade} showLabel />
                ) : (
                  <Badge
                    value={t.status}
                    label={t.status === 'locked' ? 'prerequisites incomplete' : 'not graded'}
                  />
                ),
            },
            {
              key: 'detail',
              header: 'Details',
              render: (t) =>
                t.grading ? (
                  <span>
                    {t.grading.gradedBy?.name ?? 'Unknown'} · {formatDate(t.grading.gradedAt)} ·{' '}
                    {t.grading.masteredOnAttempt
                      ? `mastered on attempt ${t.grading.masteredOnAttempt}`
                      : `attempt ${t.grading.attempts}`}{' '}
                    {t.grading.method !== 'qr' ? <Badge value={t.grading.method} /> : null}
                  </span>
                ) : t.missingPrerequisites.length ? (
                  <span className="muted">
                    Needs an A on: {t.missingPrerequisites.map((m) => m.name).join(', ')}
                  </span>
                ) : null,
            },
            { key: 'actions', header: '', render: actions },
          ]}
        />
      </Card>
      <Card title="Grading history">
        <DataTable
          rows={history.data}
          rowKey={(e) => e.id}
          empty="No grades yet."
          columns={[
            { key: 'when', header: 'When', render: gradedWhen },
            { key: 'technique', header: 'Technique', render: (e) => e.technique.name },
            {
              key: 'event',
              header: 'Grade',
              render: (e) => (
                <span className="grade-badge">
                  {e.grade ? <GradeBadge grade={e.grade} /> : <Badge value="rescinded" />}
                  {gradeEventText(e)}
                  {e.method && e.method !== 'qr' ? <Badge value={e.method} /> : null}
                </span>
              ),
            },
            { key: 'by', header: 'By', render: (e) => e.instructor?.name ?? '—' },
            { key: 'reason', header: 'Reason', render: (e) => e.reason ?? '' },
            {
              key: 'notes',
              header: 'Notes to student',
              render: (e) =>
                e.notes.length ? (
                  <ul className="grading-notes">
                    {e.notes.map((n) => (
                      <li key={n.id}>
                        {n.text}
                        <span className="muted">
                          {' '}
                          — {n.author?.name ?? 'Instructor'}, {formatDate(n.createdAt)}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  ''
                ),
            },
          ]}
        />
      </Card>
      <p>
        <Link to="/students">← All students</Link>
      </p>
    </>
  );
}
